/**
 * Main nav: Flowbite drawer on mobile; on md+ we own translate/cookie/margin (Flowbite show/hide
 * uses transform-none and fights JSP md:translate-x-0). See UiPreferenceCookies.NAV_DRAWER_OPEN.
 */
import instances from 'flowbite/lib/esm/dom/instances';

// Keep in sync with UiPreferenceCookies.NAV_DRAWER_OPEN and timeline.ts COOKIE_MAX_AGE_SECONDS
const COOKIE_NAV_DRAWER_OPEN = 'issue-tracker.nav-drawer-open';
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

const DRAWER_ID = 'logo-sidebar';
const MD_BREAKPOINT = '(min-width: 768px)';

type DrawerWithCallbacks = {
    show(): void;
    hide(): void;
    isVisible(): boolean;
    updateOnShow(cb: () => void): void;
    updateOnHide(cb: () => void): void;
};

/** Flowbite private fields — keep in sync with flowbite Drawer if upgrading. */
type DrawerInternal = {
    _visible: boolean;
    _targetEl: HTMLElement;
    _options: { bodyScrolling?: boolean };
};

function setNavDrawerOpenCookie(open: boolean): void {
    document.cookie = `${COOKIE_NAV_DRAWER_OPEN}=${open}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}`;
}

/** Default when cookie missing — matches page-with-sidebar.jsp. */
function readNavDrawerOpenCookie(): boolean {
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${COOKIE_NAV_DRAWER_OPEN.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`)
    );
    if (!match) {
        return true;
    }
    return match[1] === 'true';
}

function isShellNavDrawerOpen(): boolean {
    return document.documentElement.classList.contains('shell-nav-drawer-open');
}

function applyShellLayout(open: boolean): void {
    const root = document.documentElement;
    root.classList.toggle('shell-nav-drawer-open', open);
    root.classList.toggle('shell-nav-drawer-closed', !open);

    const main = document.getElementById('issue-tracker-main-shell');
    if (main) {
        main.classList.toggle('md:ml-64', open);
        main.classList.toggle('md:ml-0', !open);
    }

    document.querySelectorAll<HTMLButtonElement>(`[data-drawer-toggle="${DRAWER_ID}"]`).forEach((btn) => {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
}

function syncFromDrawer(open: boolean): void {
    setNavDrawerOpenCookie(open);
    applyShellLayout(open);
}

/**
 * Desktop sidebar position: JSP uses `-translate-x-full md:translate-x-0` when open; Flowbite 4 uses
 * `-translate-x-full` / `transform-none`. We set JSP classes directly and do not call Drawer.show/hide on md+.
 */
function applyDesktopSidebarTranslate(open: boolean): void {
    const sidebar = document.getElementById(DRAWER_ID);
    if (!sidebar) {
        return;
    }
    if (open) {
        sidebar.classList.remove('-translate-x-full', 'translate-x-full', 'transform-none');
        sidebar.classList.add('md:translate-x-0');
    } else {
        sidebar.classList.add('-translate-x-full');
        sidebar.classList.remove('md:translate-x-0', 'transform-none');
    }
}

function syncDrawerAria(drawer: DrawerWithCallbacks, open: boolean): void {
    const internal = drawer as unknown as DrawerInternal;
    const el = internal._targetEl;
    internal._visible = open;
    if (open) {
        el.removeAttribute('aria-hidden');
        el.setAttribute('aria-modal', 'true');
        el.setAttribute('role', 'dialog');
        if (!internal._options.bodyScrolling) {
            document.body.classList.add('overflow-hidden');
        }
    } else {
        el.setAttribute('aria-hidden', 'true');
        el.removeAttribute('aria-modal');
        el.removeAttribute('role');
        if (!internal._options.bodyScrolling) {
            document.body.classList.remove('overflow-hidden');
        }
    }
}

/** Single source of truth for md+ nav open state (cookie, margin, translate, Flowbite _visible). */
function setShellNavDrawerOpen(open: boolean, drawer: DrawerWithCallbacks): void {
    if (!isDesktopViewport()) {
        return;
    }
    applyDesktopSidebarTranslate(open);
    syncFromDrawer(open);
    syncDrawerAria(drawer, open);
}

function isDesktopViewport(): boolean {
    return window.matchMedia(MD_BREAKPOINT).matches;
}

function getDrawer(): DrawerWithCallbacks | undefined {
    return instances.getInstance('Drawer', DRAWER_ID) as DrawerWithCallbacks | undefined;
}

function handleDesktopNavDrawerToggleClick(event: MouseEvent): void {
    if (!isDesktopViewport()) {
        return;
    }
    const trigger = (event.target as Element | null)?.closest(`[data-drawer-toggle="${DRAWER_ID}"]`);
    if (!trigger) {
        return;
    }
    const drawer = getDrawer();
    if (!drawer) {
        return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    setShellNavDrawerOpen(!isShellNavDrawerOpen(), drawer);
}

function reconcileDesktopNavDrawerFromCookie(drawer: DrawerWithCallbacks): void {
    if (!isDesktopViewport()) {
        return;
    }
    setShellNavDrawerOpen(readNavDrawerOpenCookie(), drawer);
}

export function initShellNavDrawer(): void {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.updateOnShow !== 'function') {
        return;
    }

    document.addEventListener('click', handleDesktopNavDrawerToggleClick, true);

    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) {
            return;
        }
        const d = getDrawer();
        if (d) {
            reconcileDesktopNavDrawerFromCookie(d);
        }
    });

    // Flowbite may still call show/hide (Escape). Desktop toggles use handleDesktopNavDrawerToggleClick instead.
    drawer.updateOnShow(() => {
        if (isDesktopViewport()) {
            setShellNavDrawerOpen(true, drawer);
        }
    });
    drawer.updateOnHide(() => {
        if (isDesktopViewport()) {
            setShellNavDrawerOpen(false, drawer);
        }
    });

    const mdUp = window.matchMedia(MD_BREAKPOINT).matches;
    if (mdUp) {
        reconcileDesktopNavDrawerFromCookie(drawer);
    } else {
        // Small viewports: Flowbite overlay; cookie still reflects desktop preference.
        drawer.hide();
    }

    window.matchMedia(MD_BREAKPOINT).addEventListener('change', (event) => {
        if (event.matches) {
            reconcileDesktopNavDrawerFromCookie(drawer);
            return;
        }
        drawer.hide();
    });
}
